import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload } from '@mui/icons-material';
import { alpha, Button, Paper, Typography, useTheme } from '@mui/material';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { ErrorsAlert } from '@shared/components/ErrorsAlert';

import { fileSizeValidator } from '../helpers/validators';
import { getImagePreviewUrl, getVideoThumbnail } from '../helpers/media';
import ImagePreviewGrid from './ImagePreviewGrid';


interface MediaDropzoneProps {
  initialFiles: File[];
  onFilesAdded?: (files: File[]) => void;
  onFileRemoved?: (file: File) => void;
}

const MediaDropzone: React.FC<MediaDropzoneProps> = (props) => {
  const { initialFiles, onFilesAdded, onFileRemoved } = props;

  const dictionary = useDictionary();
  const theme = useTheme();
  const [files, setFiles] = useState<Tributes.FileWithPreview[]>([]);

  useEffect(() => {
    let isCancelled = false;

    const processInitialFiles = async () => {
      const processed = await Promise.all(
        initialFiles.map(async (file) => {
          let preview: string;

          if (file.type.startsWith('video/')) {
            try {
              preview = await getVideoThumbnail(file);
            } catch (err) {
              console.warn(`Could not generate thumbnail for ${file.name}`, err);
              preview = URL.createObjectURL(file);
            }
          } else {
            preview = await getImagePreviewUrl(file);
          }

          return Object.assign(file, { preview });
        }),
      );

      if (!isCancelled) {
        setFiles(processed);
      }
    };

    processInitialFiles();

    return () => {
      isCancelled = true;
      files.forEach((file) => {
        if (file.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiles]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {

    const newFiles: Tributes.FileWithPreview[] = await Promise.all(
      acceptedFiles.map(async (file) => {
        let preview: string;

        if (file.type.startsWith('video/')) {
          try {
            preview = await getVideoThumbnail(file);
          } catch (err) {
            console.warn(`Could not generate thumbnail for ${file.name}`, err);
            preview = URL.createObjectURL(file);
          }
        } else {
          preview = await getImagePreviewUrl(file);
        }

        return Object.assign(file, { preview });
      }),
    );

    onFilesAdded?.(newFiles);
    setFiles((currentFiles) => {
      const updatedFiles = [...currentFiles, ...newFiles];
      return updatedFiles;
    });
  }, [onFilesAdded]);

  const removeFile = (fileToRemove: Tributes.FileWithPreview) => {
    setFiles((currentFiles) => {
      const updatedFiles = currentFiles.filter((file) => file !== fileToRemove);

      onFileRemoved?.(fileToRemove);

      if (fileToRemove?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      return updatedFiles;
    });
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg', '.JPG', '.JPEG'],
      'image/png': ['.png', '.PNG'],
      'image/heic': ['.heic', '.HEIC'],
      'image/heif': ['.heif', '.HEIF'],
      'video/mp4': ['.mp4', '.MP4'],
      'video/quicktime': ['.mov', '.MOV'],
    },
    multiple: true,
    validator: (file) => {
      if (!fileSizeValidator(file)) {
        let fileMaxSize = '10 MB';
        if (file.type.startsWith('video/')) {
          fileMaxSize = '50 MB';
        }
        return {
          code: 'file-too-large',
          message: dictionary.forms.validations.invalidFileSize(fileMaxSize),
        };
      };
      return null;
    },
  });

  return (
    <>
      <Paper
        {...getRootProps()}
        elevation={0}
        sx={{
          p: 4,
          border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
          borderRadius: '12px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
          transition: 'background-color 0.2s ease-in-out',
          mb: 3,
        }}
      >
        <input {...getInputProps()} />

        <Upload sx={{ fontSize: 48, color: theme.palette.primary.main }} />

        <Typography variant="h6" component="p" color="textSecondary" fontSize={16}>
          Drop photos and videos here or click to browse
        </Typography>

        <Typography variant="body2" color="textSecondary" fontSize={14}>
          Supports JPG, PNG, HEIC, HEIF, MP4, MOV
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          sx={{ mt: 2, borderRadius: '4px' }}
          component="span"
        >
          Choose Files
        </Button>
      </Paper>
      {fileRejections.length > 0 && (
        <ErrorsAlert
          errorMessages={fileRejections.map(({ file, errors }) => {
            const fileErrorMessages = errors.map((error) => error.message).join('\n');
            const fullMessage = `${file.name}: ${fileErrorMessages}`;
            console.error(fullMessage);
            return fullMessage;
          })}
          title={`Rejected file${fileRejections.length > 1 ? 's' : ''}`}
          centered
        />
      )}
      <ImagePreviewGrid
        files={files}
        // @ts-ignore
        onRemove={removeFile}
        title="Previews"
        sx={{ mb: 3 }}
      />
    </>
  );
};

export default MediaDropzone;
